import { Input, type InputSlots, type SlotsToClasses } from "@heroui/react";
import React from "react";
import { type ReactElement } from "react";
import {
  type Control,
  type Path,
  Controller,
  type FieldError,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface RHFField {
  isRequired?: boolean;
  invalid?: boolean;
  error?: FieldError;
  inputRef?: React.Ref<HTMLDivElement>;
}

interface RHFWrapperProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> {
  name: TName;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, TName>;
  children: ReactElement<any>;
}

interface RHFInputProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> {
  name: TName;
  label?: string;
  disabled?: boolean;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, TName>;
  classNames?: SlotsToClasses<InputSlots>;
  format?: (value: any) => string;
}

export const RHFWrapper = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  children,
  name,
  control,
  rules,
}: RHFWrapperProps<TFieldValues, TName>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) =>
        React.cloneElement(children, {
          value: field.value,
          inputRef: field.ref,
          onChange: (val: any) => {
            field.onChange(val);
            children?.props.onChange?.(val);
          },
          invalid: fieldState.invalid,
          error: fieldState.error,
        })
      }
    />
  );
};

export const RHFInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  name,
  label,
  control,
  rules,
  classNames,
  disabled,
  format,
}: RHFInputProps<TFieldValues, TName>) => {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      disabled={disabled}
      render={({ field, fieldState: { error, invalid } }) => {
        const isRequired = invalid && error?.type === "required";
        return (
          <Input
            classNames={{
              base: twMerge("!opacity-100", classNames?.base),
              inputWrapper: twMerge(
                "group-data-[focus-visible=true]:ring-0 group-data-[focus-visible=true]:ring-offset-0 group-data-[focus-visible=true]:ring-offset-background shadow-none bg-color-fill px-4 focus-within:bg-color-fill ",
                invalid ? "border border-color-danger! z-10" : "z-9",
                classNames?.inputWrapper,
                disabled ? "bg-color-fill-3" : "",
              ),
              label: invalid ? "!text-color-danger" : "",
              input: disabled
                ? "group-data-[has-value=true]:text-color-text-2"
                : "placeholder:text-color-text-4! group-data-[has-value=true]:text-color-text-1",
            }}
            label={isRequired ? `${label}${t("v_required")}` : label}
            placeholder={isRequired ? error?.message : undefined}
            ref={field.ref}
            value={format ? format(field.value) : field.value}
            isInvalid={invalid}
            onBlur={field.onBlur}
            onChange={field.onChange}
            isDisabled={disabled}
          />
        );
      }}
    />
  );
};
